import { motion, useScroll, useSpring, useTransform, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { studioClients } from "../assets/studio/clients";
import ClientSlide from "./ClientSlide";

function MobileCarousel() {
  const [index, setIndex] = useState(0);
  const [imgIndex, setImgIndex] = useState(0);
  const client = studioClients[index];
  const images = client.imgSrcs ?? [];

  useEffect(() => {
    setImgIndex(0);
    if (images.length === 0) return;
    const interval = setInterval(() => setImgIndex((p) => (p + 1) % images.length), 4000);
    return () => clearInterval(interval);
  }, [index, images.length]);

  const handleDragEnd = (_: unknown, info: { offset: { x: number } }) => {
    if (info.offset.x < -50 && index < studioClients.length - 1) setIndex((i) => i + 1);
    else if (info.offset.x > 50 && index > 0) setIndex((i) => i - 1);
  };

  return (
    <section className="bg-white lg:hidden py-12 px-6">
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.15}
        onDragEnd={handleDragEnd}
        className="w-full cursor-grab active:cursor-grabbing"
      >
        <div className="relative aspect-video w-full overflow-hidden rounded-lg">
          <AnimatePresence mode="wait">
            <motion.img
              key={`${index}-${imgIndex}`}
              src={images[imgIndex]}
              alt={client.name}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              draggable={false}
            />
          </AnimatePresence>
        </div>

        <div className="mt-5 flex flex-col gap-2 min-h-[160px]">
          <h5 className="font-semibold font-xExpandedBold text-xl">{client.name}</h5>
          <p className="text-sm text-gray-500">{client.subtitle}</p>
          <p className="text-sm leading-relaxed mt-1">{client.desc}</p>
        </div>
      </motion.div>

      <div className="flex justify-center gap-2 mt-8">
        {studioClients.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            className={`bullet ${i === index ? "bullet-active" : ""}`}
          />
        ))}
      </div>
    </section>
  );
}

export default function ProjectShowcase() {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: targetRef });

  const totalSlides = studioClients.length;
  const steps = totalSlides - 1;

  // Snap to steps
  const snappedProgress = useTransform(scrollYProgress, (rawProgress) => {
    const stepSize = 1 / steps;
    const clampedProgress = Math.min(Math.max(rawProgress, 0), 1);
    const nearestStep = Math.round(clampedProgress / stepSize);
    return nearestStep * stepSize;
  });

  // Horizontal movement
  const xPosition = useTransform(snappedProgress, [0, 1], ["0%", `-${100 * steps}%`]);
  const springXPosition = useSpring(xPosition, { stiffness: 200, damping: 25 });

  // Current slide index
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  useMotionValueEvent(snappedProgress, "change", (snappedValue) => {
    const stepSize = 1 / steps;
    const slideIndex = Math.round(snappedValue / stepSize);
    setCurrentSlideIndex(slideIndex);
  });

  // Scroll to specific slide from bullet
  const scrollToSlide = (slideIndex: number) => {
    if (!targetRef.current) return;

    const containerElement = targetRef.current;
    const containerRect = containerElement.getBoundingClientRect();
    const containerTopOffset = window.scrollY + containerRect.top;
    const singleSlideHeight = containerRect.height / totalSlides;

    window.scrollTo({
      top: containerTopOffset + singleSlideHeight * slideIndex,
      behavior: "smooth",
    });
  };

  return (
    <>
      <MobileCarousel />

      <section
        ref={targetRef}
        className="relative bg-white hidden lg:block"
        style={{ height: `${totalSlides * 100}dvh` }}
      >
        <div className="sticky top-0 h-screen overflow-hidden flex flex-col items-center justify-center mb-10">
          {/* Slides */}
          <motion.div style={{ x: springXPosition }} className="flex h-full w-full">
            {studioClients.map((client) => (
              <ClientSlide key={client.name} client={client} />
            ))}
          </motion.div>

          {/* Bullets */}
          <div className="absolute bottom-3 lg:bottom-8 flex gap-2">
            {studioClients.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToSlide(index)}
                className={`bullet ${index === currentSlideIndex ? "bullet-active" : ""}`}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
